import React, { useMemo } from 'react';

import { MODAL_CONFIG } from '@atlassian/edition-benefits-modal/constants';
import { editionBenefitsModalEntryPoint } from '@atlassian/edition-benefits-modal/edition-benefits-modal-entrypoint';
import {
	PersonalisedVariantBoundary,
	usePersonalisedVariantValue,
} from '@atlassian/edition-benefits-modal/services/personalised-variant';
import type { VariantId } from '@atlassian/edition-benefits-modal/types';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { ModalEntryPoint } from '../shared/modal-entry-point';
import { DIALOG_CONFIG } from './dialog-config';
import { useVariantFallbackAnalytics } from './use-variant-fallback-analytics';

/**
 * Identifies the personalisation rule set evaluated in post-office template.
 */
export const VARIANT_SELECTOR_ID = 'jsm-edition-awareness-premium-upsell-modal';

/**
 * A string literal rather than `VariantIdValue.eaPillDefault` on purpose:
 * naming the registry here would load it in the navigation bundle, which is the
 * cost `PersonalisedVariantBoundary` exists to defer.
 */
const FALLBACK_VARIANT_ID: VariantId = 'ea-pill-default';

function PersonalisedUpsellTrigger(): React.JSX.Element {
	const { product, cloudId, edition } = useEditionAwareness();

	const { variantId, isResolved, fallbackReason } = usePersonalisedVariantValue();

	useVariantFallbackAnalytics({ variantId, isResolved, fallbackReason });

	const entryPointParams = useMemo(() => ({ cloudId, product }), [cloudId, product]);

	const entryPointProps = useMemo(
		() => ({
			variantId,
			touchpointId: 'edition-awareness-button' as const,
			edition,
		}),
		[variantId, edition],
	);

	return (
		<ModalEntryPoint
			entryPoint={editionBenefitsModalEntryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			dialogConfig={DIALOG_CONFIG}
			modalProps={MODAL_CONFIG}
		/>
	);
}

export function JsmPersonalisedPremiumUpsellModal(): React.JSX.Element {
	return (
		<PersonalisedVariantBoundary
			variantSelectorId={VARIANT_SELECTOR_ID}
			fallbackVariantId={FALLBACK_VARIANT_ID}
		>
			<PersonalisedUpsellTrigger />
		</PersonalisedVariantBoundary>
	);
}
